import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';

import { ResponsiveBaseComponent, JjamesButtonComponent, JJamesCardComponent } from 'shared-lib';
import { ParkingService, ParkingOperatorDto, ParkingOperatorDtoPaginationResult } from '../../../api/gen';
import { PaginationConfig } from '../../models/config/pagination-config';

import { OperatorsListComponent } from './operators-list/operators-list.component';
import { ParkingOperatorFiltersComponent, ParkingOperatorFilter } from './parking-operator-filters/parking-operator-filters.component';
import { ConfirmDeleteDialogComponent } from './confirm-delete-dialog/confirm-delete-dialog.component';
import { AssignParkingSpaceDialogComponent } from './assign-parking-space-dialog/assign-parking-space-dialog.component';
import { UnassignParkingSpaceDialogComponent } from './unassign-parking-space-dialog/unassign-parking-space-dialog.component';

@Component({
  selector: 'app-operators-ui',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSnackBarModule,
    JjamesButtonComponent,
    JJamesCardComponent,
    OperatorsListComponent
  ],
  templateUrl: './operators-ui.component.html',
  styleUrl: './operators-ui.component.scss'
})
export class OperatorsUiComponent extends ResponsiveBaseComponent implements OnInit {
  operators = signal<ParkingOperatorDtoPaginationResult | null>(null);
  pagination: PaginationConfig | null = new PaginationConfig(20, 0, 0);
  isLoading: boolean = false;
  currentFilter: ParkingOperatorFilter = {};

  constructor(
    private readonly parkingService: ParkingService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadOperators();
  }

  loadOperators(): void {
    this.isLoading = true;

    const pageIndex = this.pagination?.currentPage || 0;
    const pageSize = this.pagination?.itemsPerPage || 20;

    this.parkingService.parkingOperatorFilterPost(
      this.currentFilter.firstName,
      this.currentFilter.lastName,
      this.currentFilter.parkingSpaceId,
      pageIndex,
      pageSize,
      this.currentFilter.sortBy,
      this.currentFilter.sortOrder
    ).subscribe({
      next: (result) => {
        if (result) {
          this.operators.set(result);
          this.updatePagination(result);
        }
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading operators:', error);
        this.isLoading = false;
      }
    });
  }

  private updatePagination(data: ParkingOperatorDtoPaginationResult): void {
    if (this.pagination) {
      this.pagination.currentPage = data.pageIndex || 0;
      this.pagination.itemsPerPage = data.pageSize || 20;
      this.pagination.totalItems = data.totalCount || 0;
    }
  }

  onPageChange(event: PageEvent): void {
    if (this.pagination) {
      this.pagination.currentPage = event.pageIndex;
      this.pagination.itemsPerPage = event.pageSize;
    }
    this.loadOperators();
  }

  openFilterDialog(): void {
    const dialogRef = this.dialog.open(ParkingOperatorFiltersComponent, {
      width: this.isHandset() ? '95vw' : '500px',
      maxWidth: '95vw',
      data: { filter: this.currentFilter },
      panelClass: this.isHandset() ? 'mobile-dialog' : ''
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        this.currentFilter = result;
        if (this.pagination) {
          this.pagination.currentPage = 0; // Reset to first page when filtering
        }
        this.loadOperators();
      }
    });
  }

  onCreateOperator(): void {
    this.router.navigate(['user-management/operator-management/create']);
  }

  onEditOperator(operator: ParkingOperatorDto): void {
    // Navigate to edit operator page
    this.router.navigate(['user-management/operator-management/edit', operator.id]);
  }

  onDeleteOperator(operator: ParkingOperatorDto): void {
    const operatorName = this.getOperatorDisplayName(operator);

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: this.isHandset() ? '95vw' : '500px',
      maxWidth: '95vw',
      data: {
        title: 'Delete Parking Operator',
        message: 'Are you sure you want to delete this parking operator? This action cannot be undone.',
        itemName: operatorName,
        confirmButtonText: 'Delete Operator',
        cancelButtonText: 'Cancel'
      },
      panelClass: this.isHandset() ? 'mobile-dialog' : '',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed && operator.id) {
        this.deleteOperator(operator.id, operatorName);
      }
    });
  }

  private deleteOperator(operatorId: string, operatorName: string): void {
    this.parkingService.parkingOperatorParkingOperatorIdDelete(operatorId).subscribe({
      next: (result) => {
        if (result) {
          this.snackBar.open(
            `Operator "${operatorName}" has been successfully deleted.`,
            'Close',
            {
              duration: 5000,
              panelClass: ['success-snackbar']
            }
          );
          // Reload the operators list
          this.loadOperators();
        }
      },
      error: (error: any) => {
        console.error('Error deleting operator:', error);
        this.showDeleteError(operatorName, error.message || 'Failed to delete operator');
      }
    });
  }

  private showDeleteError(operatorName: string, errorMessage: string): void {
    this.snackBar.open(
      `Failed to delete operator "${operatorName}": ${errorMessage}`,
      'Close',
      {
        duration: 8000,
        panelClass: ['error-snackbar']
      }
    );
  }

  private getOperatorDisplayName(operator: ParkingOperatorDto): string {
    const firstName = operator.firstName || '';
    const lastName = operator.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || `Operator ${operator.id?.substring(0, 8)}` || 'Unknown Operator';
  }

  onViewOperatorDetails(operator: ParkingOperatorDto): void {
    // TODO: Navigate to operator details page or open details dialog
    console.log('View operator details:', operator);
  }

  onAssignParkingSpace(operator: ParkingOperatorDto): void {
    const dialogRef = this.dialog.open(AssignParkingSpaceDialogComponent, {
      width: this.isHandset() ? '95vw' : '500px',
      maxWidth: '95vw',
      data: { operator },
      panelClass: this.isHandset() ? 'mobile-dialog' : '',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // Assignment was successful, reload the operators list to show updated data
        this.loadOperators();

        // Show success message
        const operatorName = this.getOperatorDisplayName(operator);
        this.snackBar.open(
          `Parking space successfully assigned to ${operatorName}`,
          'Close',
          {
            duration: 5000,
            panelClass: ['success-snackbar']
          }
        );
      }
    });
  }

  onUnassignParkingSpace(operator: ParkingOperatorDto): void {
    const dialogRef = this.dialog.open(UnassignParkingSpaceDialogComponent, {
      width: this.isHandset() ? '95vw' : '500px',
      maxWidth: '95vw',
      data: { operator },
      panelClass: this.isHandset() ? 'mobile-dialog' : '',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // Unassignment was successful, reload the operators list to show updated data
        this.loadOperators();

        // Show success message
        const operatorName = this.getOperatorDisplayName(operator);
        this.snackBar.open(
          `Parking space successfully unassigned from ${operatorName}`,
          'Close',
          {
            duration: 5000,
            panelClass: ['success-snackbar']
          }
        );
      }
    });
  }
}
